import { CalendarClock, FileText, IdCard, TrendingUp, Wallet, Banknote } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { allowedTabs, resolveTab, type PermissionCheck } from '@/lib/auth/permissions'
import { HR_PAYROLL_READ_PERM, HR_WRITE_PERM } from '../permissions'
import { HrSurface } from './components/HrSurface'
import { MOBILE_TABS_LIST, MOBILE_TABS_TRIGGER } from './components/mobileTabs'
import { ProfileCard } from './components/ProfileCard'
import { useManager, usePersonelView } from './hooks/usePersonelStore'
import { BelgelerTab } from './tabs/BelgelerTab'
import { GenelBilgilerTab } from './tabs/GenelBilgilerTab'
import { IzinMesaiTab } from './tabs/IzinMesaiTab'
import { OdemeGecmisiTab } from './tabs/OdemeGecmisiTab'
import { PerformansTab } from './tabs/PerformansTab'
import { UcretBordroTab } from './tabs/UcretBordroTab'
import type { DetailTab } from './types'

const TABS: { value: DetailTab; icon: typeof IdCard }[] = [
  { value: 'genel', icon: IdCard },
  { value: 'ucret', icon: Wallet },
  { value: 'izin', icon: CalendarClock },
  { value: 'belge', icon: FileText },
  { value: 'odeme', icon: Banknote },
  { value: 'perf', icon: TrendingUp },
]

const TAB_PERMS: Partial<Record<DetailTab, PermissionCheck>> = { ucret: HR_PAYROLL_READ_PERM, odeme: HR_PAYROLL_READ_PERM }

const isTab = (v: string | null): v is DetailTab => TABS.some((x) => x.value === v)

export function PersonelDetailPage() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()
  const [params, setParams] = useSearchParams()
  const raw = params.get('sekme')
  const allowed = allowedTabs(TABS.map((x) => x.value), TAB_PERMS, can)
  const tab = resolveTab<DetailTab>(isTab(raw) ? raw : 'genel', allowed)
  // Yalnızca açık sekmenin verisi (ve kullanıcının yetkisi olan bölümler) çekilir.
  const view = usePersonelView(id, tab)
  const { personel, profile, detail } = view
  const manager = useManager(profile?.managerId ?? '')

  if (view.isLoading) {
    return (
      <HrSurface>
        <div className="py-20 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
      </HrSurface>
    )
  }
  if (!personel || !profile || !detail) return <Navigate to="/hr/personel" replace />

  const changeTab = (v: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (v === 'genel') next.delete('sekme')
        else next.set('sekme', v)
        return next
      },
      { replace: true },
    )

  return (
    <HrSurface>
      <PageHeader
        backTo="/hr/personel"
        eyebrow={
          <span className="flex items-center gap-2">
            <Link to="/hr/personel" className="hover:text-foreground">
              {t('hr.personel.backToList')}
            </Link>
            <span className="opacity-50">/</span>
            <span className="text-foreground">{personel.ad}</span>
          </span>
        }
        title={t('hr.personel.detailTitle')}
      />

      <Tabs key={personel.id} value={tab} onValueChange={changeTab} className="space-y-4">
        <ProfileCard
          personel={personel}
          kidem={profile.kidem}
          manager={manager}
          onEdit={can(HR_WRITE_PERM) ? () => navigate(`/hr/personel/${personel.id}/duzenle`) : undefined}
        >
          <TabsList aria-label={t('hr.personel.detailTitle')} className={MOBILE_TABS_LIST}>
            {TABS.filter((x) => allowed.includes(x.value)).map(({ value, icon: Icon }) => (
              <TabsTrigger key={value} value={value} className={MOBILE_TABS_TRIGGER}>
                <Icon />
                {t(`hr.personel.tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </ProfileCard>

        <TabsContent value="genel" className="mt-0">
          <GenelBilgilerTab personel={personel} profile={profile} detail={detail} manager={manager} />
        </TabsContent>
        {allowed.includes('ucret') ? (
          <TabsContent value="ucret" className="mt-0">
            <UcretBordroTab personel={personel} ucret={detail.ucret} sigorta={profile.sigorta} />
          </TabsContent>
        ) : null}
        <TabsContent value="izin" className="mt-0">
          <IzinMesaiTab personelId={personel.id} izin={detail.izin} />
        </TabsContent>
        <TabsContent value="belge" className="mt-0">
          <BelgelerTab personel={personel} belge={detail.belge} />
        </TabsContent>
        {allowed.includes('odeme') ? (
          <TabsContent value="odeme" className="mt-0">
            <OdemeGecmisiTab personel={personel} odeme={detail.odeme} />
          </TabsContent>
        ) : null}
        <TabsContent value="perf" className="mt-0">
          <PerformansTab personel={personel} perf={detail.perf} />
        </TabsContent>
      </Tabs>
    </HrSurface>
  )
}
